import { Component, EventEmitter, Input, Output, OnDestroy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactListService, ContactItem, ContactListList } from 'com.trumbull.phx.contactlist.api-client';
import { IPSSearchComponent } from '@ips/common-angular';
import { Subject, takeUntil, switchMap, of, catchError, timeout } from 'rxjs';
import { LoaderService } from '../../../services/loader-service/loader.service';
import { LoaderComponent } from '../../../loader/loader.component';

@Component({
  selector: 'app-contact-search',
  standalone: true,
  imports: [CommonModule, IPSSearchComponent, LoaderComponent],
  templateUrl: './contact-search.component.html',
  styleUrls: ['./contact-search.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ContactSearchComponent implements OnDestroy {
  @Input() providerId: string = 'standard';
  @Input() timeoutMs: number = 10000; 
  @Input() minTermLength: number = 3;
  @Input() pageSize: number = 10;
  @Output() onSelect = new EventEmitter<ContactItem>();

  results: ContactItem[] = [];
  hasMore: boolean = false;
  isLoadingMore: boolean = false;
  contactListId: any = '';
  private currentPage: number = 1;
  private currentSearchTerm: string = '';
  private searchTerms = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private contactListService: ContactListService, private loaderService: LoaderService) {
      /* this.contactListService.listContactLists(this.providerId, '', undefined,'provider','',undefined,undefined,'')
        .subscribe((response: ContactListList) => {
          this.contactListId = response?.contactList?.[0]?.id;
        }); */
      this.searchTerms.pipe(
          takeUntil(this.destroy$),
          switchMap((term: string) => {
            this.loaderService.show();
              if (!term || term.length < this.minTermLength) {
                  //this.results = [];
                  this.hasMore = false;
                  this.currentPage = 1;
                  this.currentSearchTerm = '';
                  return of({ contactList: [], count: 0 } as ContactListList);
              }
              // Reset for new search
              this.currentPage = 1;
              this.currentSearchTerm = term;
              //this.results = [];
              return this.contactListService.searchContacts(
                  this.providerId, // providerId
                  this.contactListId, // contactListId
                  term, // contactName
                  '', // accountNames
                  '', // clientNames
                  '', // status
                  '', // partyid
                  'provider', // tagName
                  '', // contactId
                  'Organization', // typeCode
                  '', // sort
                  this.currentPage, // page
                  this.pageSize // pageSize
              ).pipe(
                  timeout(this.timeoutMs), 
                  catchError(error => {
                      console.error('Search failed or timed out', error);
                      this.loaderService.hide();
                      return of({ contactList: [], count: 0 } as ContactListList);
                  })
              );
          })
      ).subscribe((response: ContactListList) => {
          this.results = [];
          const items = response.contactList?.flatMap(list => list.contactItems || []) || [];
          this.results = items;
          this.hasMore = items.length >= this.pageSize;
          this.loaderService.hide();
      });
  }

  ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
  }

  search(event: any) {
    const query = event.query;
    this.searchTerms.next(query);
  }

  onSelectionChange(value: ContactItem) {
    this.onSelect.emit(value);
  }

  loadMore() {
    if (this.isLoadingMore || !this.hasMore || !this.currentSearchTerm) {
      return;
    }

    this.isLoadingMore = true;
    this.currentPage++;

    this.loaderService.show();
    this.contactListService.searchContacts(
      this.providerId, 
      this.contactListId,
      this.currentSearchTerm, 
      '', // accountNames
      '', // clientNames
      '', // status
      '', // partyid
      'provider', // tagName
      '', // contactId
      'Organization', // typeCode
      '', // sort
      this.currentPage, // page
      this.pageSize // pageSize
    ).pipe(
      timeout(this.timeoutMs),
      catchError(error => {
        console.error('Load more failed or timed out', error);
        this.isLoadingMore = false;
        this.currentPage--;
        return of({ contactList: [], count: 0 } as ContactListList);
      })
    ).subscribe((response: ContactListList) => {
      const items = response.contactList?.flatMap(list => list.contactItems || []) || [];
      this.results = [...this.results, ...items];
      this.hasMore = items.length >= this.pageSize;
      this.isLoadingMore = false;
      this.loaderService.hide();
    });
  }

  getContactAddress(contact: ContactItem): string {
    const address = contact.party?.locators?.find(l => l.typeCode === 'location')?.location?.location?.usAddress;
    if (address) {
        return [address.lineOne, address.lineTwo, address.city, address.stateCode, address.postalCode].filter(p => !!p).join(', ');
    }
    return '';
  }

  getContactPhone(contact: ContactItem): string {
      const phone = contact.party?.locators?.find(l => l.typeCode === 'phone' && l.phone?.purpose === 'work')?.phone?.number;
      return phone || '';
  }
}
